// Atomic: Error handling utilities

export enum ErrorType {
  DATA_LOADING = 'DATA_LOADING',
  DATA_NOT_FOUND = 'DATA_NOT_FOUND',
  INVALID_INPUT = 'INVALID_INPUT',
  NETWORK_ERROR = 'NETWORK_ERROR',
  CALCULATION_ERROR = 'CALCULATION_ERROR',
  UNKNOWN = 'UNKNOWN'
}

export interface ServiceError {
  type: ErrorType;
  message: string;
  userMessage: string;
  details?: unknown;
}

/**
 * Create a user-friendly error message
 */
export function createUserFriendlyError(error: unknown, context: string): ServiceError {
  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      type: ErrorType.NETWORK_ERROR,
      message: error.message,
      userMessage: 'Verbindungsfehler: Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
      details: error
    };
  }

  // Data not found
  if (error instanceof Error && error.message.includes('404')) {
    return {
      type: ErrorType.DATA_NOT_FOUND,
      message: error.message,
      userMessage: 'Die angeforderten Daten konnten nicht gefunden werden. Bitte wählen Sie eine andere Gemeinde.',
      details: error
    };
  }

  // Invalid input
  if (error instanceof Error && error.message.includes('Ungültig')) {
    return {
      type: ErrorType.INVALID_INPUT,
      message: error.message,
      userMessage: error.message, // Already user-friendly
      details: error
    };
  }

  // Calculation errors
  if (context.includes('calculation') || context.includes('berechnung')) {
    return {
      type: ErrorType.CALCULATION_ERROR,
      message: error instanceof Error ? error.message : 'Unknown calculation error',
      userMessage: 'Bei der Berechnung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
      details: error
    };
  }

  // Data loading errors
  if (context.includes('load') || context.includes('data')) {
    return {
      type: ErrorType.DATA_LOADING,
      message: error instanceof Error ? error.message : 'Unknown data loading error',
      userMessage: 'Die Daten konnten nicht geladen werden. Bitte aktualisieren Sie die Seite.',
      details: error
    };
  }

  // Default error
  return {
    type: ErrorType.UNKNOWN,
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    details: error
  };
}

/**
 * Log error with context
 */
export function logError(context: string, error: unknown): void {
  const timestamp = new Date().toISOString();
  const errorInfo = {
    timestamp,
    context,
    error: error instanceof Error ? {
      message: error.message,
      stack: error.stack
    } : error
  };

  // In development, log to console
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, errorInfo);
  }

  // In production, you might want to send to an error tracking service
  // Example: Sentry.captureException(error, { extra: errorInfo });
}

/**
 * Retry failed operations with exponential backoff
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (error instanceof Error && 
          (error.message.includes('404') || 
           error.message.includes('Ungültig'))) {
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}